import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function PayerRequestRefund() {
  const { t } = useTranslation();

  const [availableWallets, setAvailableWallets] = useState([]);
  const [routeMoneyBoxWallets, setRouteMoneyBoxWallets] = useState([]);
  const [selectedRouteMoneyBoxWalletId, setSelectedRouteMoneyBoxWalletId] = useState("");

  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchAvailableWallets();
    fetchRouteMoneyBoxWallets();
  }, []);

  const fetchAvailableWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/available-wallets`);
      setAvailableWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch available wallets", err);
      setAvailableWallets([]);
    }
  };

  const fetchRouteMoneyBoxWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/refundable-wallets`);
      setRouteMoneyBoxWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch route money-box wallets", err);
      setRouteMoneyBoxWallets([]);
    }
  };

  const handleRefundRequest = async () => {
    const routeMoneyBoxWallet = routeMoneyBoxWallets.find(
      (wallet) => Number(wallet.wallet_id) === Number(selectedRouteMoneyBoxWalletId)
    );

    if (!routeMoneyBoxWallet) {
      setMessage(t("payer_request_refund.messages.no_vendor_wallet"));
      setMessageVariant("error");
      return;
    }

    const matchingPayerWallet = availableWallets.find(
      (wallet) => Number(wallet.route_id) === Number(routeMoneyBoxWallet.route_id)
    );

    if (!matchingPayerWallet) {
      setMessage(t("payer_request_refund.messages.no_matching_wallet"));
      setMessageVariant("error");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/payer/request-ticket-refund`, {
        from_wallet_id: routeMoneyBoxWallet.wallet_id,
        delta_tickets_from_wallet: -1,
        delta_money_from_wallet: 0,
        to_wallet_id: matchingPayerWallet.wallet_id,
        delta_tickets_to_wallet: 1,
        delta_money_to_wallet: 0,
        header: "request-ticket-refund",
      });

      setMessage(t("payer_request_refund.messages.success"));
      setMessageVariant("success");

      await fetchAvailableWallets();
      await fetchRouteMoneyBoxWallets();
    } catch (err) {
      console.error("❌ Failed to request refund", err);
      setMessage(err?.response?.data?.detail || t("payer_request_refund.messages.failure"));
      setMessageVariant("error");
    } finally {
      setSelectedRouteMoneyBoxWalletId("");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_request_refund.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("payer_request_refund.title")}>
              <Stack gap="3">
                <FormField label={t("payer_request_refund.fields.wallet.label")}>
                  <SelectInput
                    value={selectedRouteMoneyBoxWalletId}
                    onChange={(e) => {
                      setSelectedRouteMoneyBoxWalletId(e.target.value);
                      setLastAction("");
                      setMessage("");
                      setMessageVariant("info");
                    }}
                  >
                    <option value="">
                      {t("payer_request_refund.fields.wallet.placeholder")}
                    </option>

                    {routeMoneyBoxWallets.map((wallet) => {
                      const payerWallet = availableWallets.find(
                        (w) => Number(w.route_id) === Number(wallet.route_id)
                      );

                      return (
                        <option key={wallet.wallet_id} value={wallet.wallet_id}>
                          {[
                            wallet.vendor_name,
                            wallet.route_name,
                            wallet.start_point && wallet.end_point
                              ? `${wallet.start_point} → ${wallet.end_point}`
                              : null,
                            payerWallet
                              ? `${t("payer_request_refund.dropdown_item.ticket_balance_now")}: ${payerWallet.ticket_balance ?? 0}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </option>
                      );
                    })}
                  </SelectInput>
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("refund");
                    handleRefundRequest();
                  }}
                  disabled={
                    !selectedRouteMoneyBoxWalletId ||
                    (lastAction === "refund" && message !== "")
                  }
                  fullWidth
                >
                  {lastAction === "refund"
                    ? messageVariant === "success"
                      ? t("payer_request_refund.actions.success")
                      : messageVariant === "error" && message
                      ? `${t("payer_request_refund.actions.failure")}: ${message}`
                      : t("payer_request_refund.actions.submit")
                    : t("payer_request_refund.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}